import { useCallback, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from './useBreakpoint';

const DEFAULT_THRESHOLD = 300;
const THROTTLE_DELAY = 150;

/**
 * Options for the useScrollTrigger hook.
 */
type ScrollTriggerOptions = {
  threshold?: number;
  enabled?: boolean;
};

/**
 * A hook that detects if the user is scrolling down past a certain threshold on mobile.
 * Activation is controlled by the enabled flag (if passed) AND the viewport being mobile.
 * @param options Configuration options for the hook.
 * @returns boolean - True if actively scrolling down past threshold, false otherwise or if not active.
 */
export function useScrollTrigger(options?: ScrollTriggerOptions): boolean {
  const { isMobile } = useBreakpoint();
  // const { isLoading } = useContext(JobsLoadingContext);
  const { threshold = DEFAULT_THRESHOLD, enabled = true } = options || {};

  const isActive = enabled && isMobile;

  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollY = useRef(0);
  const isThrottled = useRef(false);

  const handleScroll = useCallback(() => {
    if (!isActive) return;

    if (!isThrottled.current) {
      const currentScrollY = window.scrollY;
      const deltaY = currentScrollY - lastScrollY.current;
      const scrollingDown = deltaY > 10;

      let newState = false;
      if (scrollingDown && currentScrollY > threshold) {
        newState = true;
      }

      setIsScrollingDown(newState);

      lastScrollY.current = currentScrollY > 0 ? currentScrollY : 0;

      isThrottled.current = true;
      setTimeout(() => {
        isThrottled.current = false;
      }, THROTTLE_DELAY);
    }
  }, [isActive, threshold]);

  useEffect(() => {
    if (!isActive) {
      setIsScrollingDown(false);
      return;
    }

    // Hook is now active
    const currentScrollY = window.scrollY;
    setIsScrollingDown(false); // Ensure initial state is NOT hidden when activated
    lastScrollY.current = currentScrollY; // Set the baseline for future scrolls

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isActive, threshold, handleScroll]);

  return isActive ? isScrollingDown : false;
}
